/**
 * SetupWizard -- 3-step onboarding modal.
 *
 * Steps:
 * 0 -- Welcome + Name
 * 1 -- Timezone + Working Hours
 * 2 -- First Project
 *
 * Uses the same backdrop/panel pattern as TaskDetailPopover.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import type { UserProfile } from "../layout/sidebar/types";

interface SetupWizardProps {
  isOpen: boolean;
  profile: UserProfile | null;
  onUpdateProfile: (updates: Partial<UserProfile>) => Promise<UserProfile>;
  onComplete: () => void;
  onDismiss: () => void;
}

const PROJECT_CHIPS = ["Work", "Personal", "Side Project"];

/**
 * Compute which step to start on based on existing profile data.
 */
function computeInitialStep(profile: UserProfile | null): number {
  if (!profile?.name) return 0;
  if (!profile?.timezone || !profile?.workingHours?.start || !profile?.workingHours?.end) return 1;
  return 2;
}

export function SetupWizard({
  isOpen,
  profile,
  onUpdateProfile,
  onComplete,
  onDismiss,
}: SetupWizardProps): React.ReactElement {
  const panelRef = useRef<HTMLDivElement>(null);
  const initialStepComputed = useRef(false);

  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [workStart, setWorkStart] = useState("09:00");
  const [workEnd, setWorkEnd] = useState("17:00");
  const [projectName, setProjectName] = useState("");
  const [saving, setSaving] = useState(false);

  // Populate from profile when opened
  useEffect(() => {
    if (isOpen && profile && !initialStepComputed.current) {
      initialStepComputed.current = true;
      setStep(computeInitialStep(profile));
      if (profile.name) setName(profile.name);
      if (profile.timezone) setTimezone(profile.timezone);
      if (profile.workingHours?.start) setWorkStart(profile.workingHours.start);
      if (profile.workingHours?.end) setWorkEnd(profile.workingHours.end);
    }
    if (!isOpen) {
      initialStepComputed.current = false;
    }
  }, [isOpen, profile]);

  // Escape to dismiss
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === "Escape") onDismiss();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onDismiss]);

  // Focus panel on open
  useEffect(() => {
    if (isOpen && panelRef.current) {
      panelRef.current.focus();
    }
  }, [isOpen, step]);

  const handleNext = useCallback(async () => {
    setSaving(true);
    try {
      if (step === 0) {
        await onUpdateProfile({ name: name.trim() });
        setStep(1);
      } else if (step === 1) {
        await onUpdateProfile({
          timezone,
          workingHours: { start: workStart, end: workEnd },
        });
        setStep(2);
      } else if (step === 2) {
        const existingProjects = profile?.projects ?? [];
        if (projectName.trim()) {
          const newProject = { id: crypto.randomUUID(), name: projectName.trim() };
          await onUpdateProfile({
            projects: [...existingProjects, newProject],
            onboardingComplete: true,
          });
        } else {
          await onUpdateProfile({ onboardingComplete: true });
        }
        onComplete();
      }
    } finally {
      setSaving(false);
    }
  }, [step, name, timezone, workStart, workEnd, projectName, profile, onUpdateProfile, onComplete]);

  const handleBack = useCallback(() => {
    setStep((s) => Math.max(0, s - 1));
  }, []);

  // Enter to advance
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !saving) {
        if (step === 0 && !name.trim()) return;
        handleNext();
      }
    },
    [step, name, saving, handleNext]
  );

  if (!isOpen) return <></>;

  const stepIndicator = (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={[
            "w-2.5 h-2.5 rounded-full transition-colors duration-200",
            i < step ? "bg-primary" : i === step ? "border-2 border-primary" : "bg-surface-hover",
          ].join(" ")}
        />
      ))}
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onDismiss} />

      {/* Panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] max-w-md bg-surface border border-border rounded-xl shadow-lg focus:outline-none p-6"
      >
        {stepIndicator}

        {/* Step 0: Welcome + Name */}
        {step === 0 && (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-text-primary">Welcome to Atlas</h2>
              <p className="text-sm text-text-secondary mt-1">
                Your AI-powered task manager. Let's get you set up in under a minute.
              </p>
            </div>
            <div>
              <label htmlFor="wizard-name" className="block text-sm font-medium text-text-secondary mb-1">
                What should I call you?
              </label>
              <input
                id="wizard-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-bg border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
                autoFocus
                placeholder="Your name"
              />
            </div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={onDismiss}
                className="text-xs text-text-tertiary hover:text-text-secondary transition-colors"
              >
                Skip for now
              </button>
              <button
                type="button"
                onClick={handleNext}
                disabled={!name.trim() || saving}
                className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Timezone + Working Hours */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-text-primary">When do you work?</h2>
              <p className="text-sm text-text-secondary mt-1">
                This helps Atlas schedule your tasks at the right times.
              </p>
            </div>
            <div>
              <label htmlFor="wizard-tz" className="block text-sm font-medium text-text-secondary mb-1">
                Timezone
              </label>
              <select
                id="wizard-tz"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-bg border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
              >
                {Intl.supportedValuesOf("timeZone").map((tz) => (
                  <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <label htmlFor="wizard-start" className="block text-sm font-medium text-text-secondary mb-1">
                  Start
                </label>
                <input
                  id="wizard-start"
                  type="time"
                  value={workStart}
                  onChange={(e) => setWorkStart(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-bg border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="wizard-end" className="block text-sm font-medium text-text-secondary mb-1">
                  End
                </label>
                <input
                  id="wizard-end"
                  type="time"
                  value={workEnd}
                  onChange={(e) => setWorkEnd(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-bg border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={onDismiss}
                className="text-xs text-text-tertiary hover:text-text-secondary transition-colors"
              >
                Skip for now
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2 text-xs font-medium text-text-secondary bg-surface-hover hover:bg-surface-active rounded-lg transition-colors"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={saving}
                  className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: First Project */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-text-primary">Create your first project</h2>
              <p className="text-sm text-text-secondary mt-1">
                Projects help organize your tasks. You can always add more later.
              </p>
            </div>
            <div>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-bg border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
                autoFocus
                placeholder="Project name"
              />
              <div className="flex flex-wrap gap-2 mt-2">
                {PROJECT_CHIPS.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => setProjectName(chip)}
                    className="bg-primary-subtle text-primary border border-primary/20 rounded-xl text-xs px-3 py-1.5 hover:bg-primary/20 transition-colors cursor-pointer"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={onDismiss}
                className="text-xs text-text-tertiary hover:text-text-secondary transition-colors"
              >
                Skip for now
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2 text-xs font-medium text-text-secondary bg-surface-hover hover:bg-surface-active rounded-lg transition-colors"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={saving}
                  className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
